import {
  type CollectionReference,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  collection,
  doc,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore'

import { getFirebaseFirestore } from '@/lib/firebase/app'
import { type Subscription, subscribeCollection, subscribeDoc } from '@/lib/firebase/subscribe'
import type { RunStageEntryBase } from '@/types/plan'
import { BRAND_DOC_STATUSES, type BrandDocMeta } from '@/types/brand-doc-meta'
import {
  RUN_ENTRY_STEPS,
  RUN_STATUSES,
  type RunEntry,
  type RunStatus,
  type SetupRun,
} from '@/types/setup-run'
import type { FactSource } from '@/types/setup-source'

export interface SetupScope {
  workspaceId: string
  brandId: string
}

export function setupCollection(
  scope: SetupScope,
  name: string,
): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    scope.workspaceId,
    'brands',
    scope.brandId,
    name,
  )
}

export function asIsoOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function asStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry !== '')
    : []
}

export function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function isRecord(value: unknown): value is DocumentData {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The value as a record; anything else reads as an empty one. */
export function asRecord(value: unknown): DocumentData {
  return isRecord(value) ? value : {}
}

export function asRecords(value: unknown): DocumentData[] {
  return Array.isArray(value) ? value.filter((entry): entry is DocumentData => isRecord(entry)) : []
}

export function asOneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

export function toBrandDocMeta(value: unknown): BrandDocMeta {
  const meta = isRecord(value) ? value : {}
  return {
    version: asNumber(meta.version),
    status: asOneOf(meta.status, BRAND_DOC_STATUSES, 'draft'),
    createdAt: asIsoOrNull(meta.createdAt),
    updatedAt: asIsoOrNull(meta.updatedAt),
    confirmedAt: asIsoOrNull(meta.confirmedAt),
  }
}

export function toSources(value: unknown): FactSource[] {
  return asRecords(value).map((source) => ({
    url: asString(source.url),
    title: asString(source.title),
  }))
}

export function toRunEntry(entry: DocumentData): RunEntry {
  return {
    id: asString(entry.id),
    step: asOneOf(entry.step, RUN_ENTRY_STEPS, 'queued'),
    attempts: asNumber(entry.attempts),
    ...(typeof entry.willRetry === 'boolean' ? { willRetry: entry.willRetry } : {}),
    ...(typeof entry.errorCode === 'string' ? { errorCode: entry.errorCode } : {}),
    ...(typeof entry.startedAt === 'string' ? { startedAt: entry.startedAt } : {}),
    ...(typeof entry.completedAt === 'string' ? { completedAt: entry.completedAt } : {}),
  }
}

/** The fields every stage entry of a stage-run document carries. */
export function toStageEntryBase(entry: DocumentData): RunStageEntryBase {
  return {
    ...toRunEntry(entry),
    seq: asNumber(entry.seq),
    ...(typeof entry.deadlineAt === 'string' ? { deadlineAt: entry.deadlineAt } : {}),
    ...(typeof entry.outputPath === 'string' ? { outputPath: entry.outputPath } : {}),
  }
}

/** The fields every run document carries; an unknown stage reads as the first, `toEntry` returning `undefined` drops that entry. */
export function toSetupRun<Stage extends string, Entry extends RunEntry>(
  id: string,
  data: DocumentData,
  stages: readonly [Stage, ...Stage[]],
  toEntry: (entry: DocumentData) => Entry | undefined,
): SetupRun<Stage, Entry> {
  return {
    id,
    brandId: asString(data.brandId),
    status: asOneOf(data.status, RUN_STATUSES, 'running'),
    stage: asOneOf(data.stage, stages, stages[0]),
    entries: asRecords(data.entries)
      .map(toEntry)
      .filter((entry): entry is Entry => entry !== undefined),
    ...(typeof data.errorCode === 'string' ? { errorCode: data.errorCode } : {}),
    createdAt: asIsoOrNull(data.createdAt),
    updatedAt: asIsoOrNull(data.updatedAt),
    completedAt: asIsoOrNull(data.completedAt),
    cancelledAt: asIsoOrNull(data.cancelledAt),
  }
}

/** Live view of the most recent run in a brand collection by `createdAt`, or of the most recent with `status` when given. */
export function subscribeLatestRun<R extends SetupRun>(
  scope: SetupScope,
  name: string,
  toRun: (snapshot: DocumentSnapshot<DocumentData>) => R | undefined,
  status?: RunStatus,
): Subscription<R | null> {
  const filters = status === undefined ? [] : [where('status', '==', status)]
  return subscribeCollection(
    query(setupCollection(scope, name), ...filters, orderBy('createdAt', 'desc'), limit(1)),
    (snapshot) => {
      const [latest] = snapshot.docs
      return latest ? (toRun(latest as QueryDocumentSnapshot<DocumentData>) ?? null) : null
    },
  )
}

/** Live view of every document of a brand collection; `toItem` returning `undefined` drops that document. */
export function subscribeAll<T>(
  scope: SetupScope,
  name: string,
  toItem: (id: string, data: DocumentData) => T | undefined,
): Subscription<T[]> {
  return subscribeCollection(setupCollection(scope, name), (snapshot) =>
    snapshot.docs.flatMap((docSnapshot) => {
      const item = toItem(docSnapshot.id, docSnapshot.data())
      return item === undefined ? [] : [item]
    }),
  )
}

/** Live view of one document of a brand collection; `toItem` maps it, and an absent document reads as `undefined`. */
export function subscribeOne<T>(
  scope: SetupScope,
  name: string,
  id: string,
  toItem: (id: string, data: DocumentData) => T,
): Subscription<T> {
  return subscribeDoc(doc(setupCollection(scope, name), id), (snapshot) => {
    const data = snapshot.data()
    return data ? toItem(snapshot.id, data) : undefined
  })
}
